import { Alert, AlertProps, AlertTitle } from '@mui/material';
import { toast as toastOrigin, Toaster, ToasterProps, ToastOptions } from 'react-hot-toast';

type CustomToastOptions = ToastOptions & {
  message: string;
  title?: string;
  alertProps?: Partial<Omit<AlertProps, 'severity' | 'onClose'>>;
};

function createHandler(color: AlertProps['severity']) {
  return ({ title, message, alertProps, ...originOptions }: CustomToastOptions) => {
    return toastOrigin.custom((t) => {
      const close = () => toastOrigin.remove(t.id);

      return (
        <Alert
          {...t.ariaProps}
          {...alertProps}
          sx={{ minWidth: 300, whiteSpace: 'break-spaces' }}
          variant={'filled'}
          severity={color}
          onClose={close}
        >
          {title ? <AlertTitle>{title}</AlertTitle> : null}
          {message}
        </Alert>
      );
    }, originOptions);
  };
}

/*
 * Re-define origin toast to custom names.
 *
 * Examples of usage:
 * notify.positive({message: "My own message", duration: 5000, headline: "My Headline"});
 * */
const notification = {
  positive: createHandler('success'),
  negative: createHandler('error'),
  warning: createHandler('warning'),
  neutral: createHandler('info'),
} satisfies Record<string, ReturnType<typeof createHandler>>;

const actions = {
  remove: (notifyId: string) => toastOrigin.remove(notifyId),
  dismiss: (notifyId: string) => toastOrigin.dismiss(notifyId),
};

const notify = { ...notification, ...actions };

type NotificationsProviderProps = {
  position?: ToasterProps['position'];
  style?: ToasterProps['containerStyle'];
  className?: ToasterProps['containerClassName'];
  toastOptions?: Pick<ToastOptions, 'duration' | 'ariaProps'>;
};
const NotificationsProvider = ({
  position = 'top-right',
  style,
  className,
  toastOptions = {},
}: NotificationsProviderProps) => {
  return (
    <Toaster
      position={position}
      containerStyle={style}
      containerClassName={className}
      toastOptions={
        {
          duration: toastOptions.duration || 5000,
          ariaProps: toastOptions.ariaProps,
        } as typeof toastOptions
      }
    />
  );
};

export { NotificationsProvider, notify };
